import { Divider } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Autocomplete from '@material-ui/lab/Autocomplete';
import axios from 'axios';
import { debounce } from 'debounce';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import * as qs from 'qs';
import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../api/WindsorApi';
import AwsRegions from '../../common/AwsRegions';
import LoadingIndicator from '../../common/LoadingIndicator';
import NoResultsText from '../../common/NoResultsText';
import MobxContext from '../../mobx/MobxContext';
import FaroDeploymentLandingPageSearch from './FaroDeploymentLandingPageSearch';
import FaroDeploymentsTable from './FaroDeploymentsTable';

const CancelToken = axios.CancelToken;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    searchTextField: {
      backgroundColor: theme.palette.background.paper,
      [theme.breakpoints.up('md')]: {
        marginLeft: 16,
        maxWidth: 300
      }
    },
    toolbar: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'space-between'
      }
    },
    toolbarActions: {
      marginBottom: 8,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        marginBottom: 0
      }
    },
    environmentAutocomplete: {
      backgroundColor: theme.palette.background.paper,
      marginBottom: 8,
      minWidth: 200,
      [theme.breakpoints.up('md')]: {
        marginRight: 16,
        marginBottom: 0
      }
    },
    regionAutocomplete: {
      backgroundColor: theme.palette.background.paper,
      minWidth: 200
    }
  })
);

const windsorApi = new WindsorApi();

function FaroDeploymentSearchPage(props: RouteComponentProps<any>) {
  const { history, location } = props;
  const classes = useStyles({});
  const { environmentName, region, search = '' } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const [deployments, setDeployments] = useState(null);
  const [page, setPage] = useState(0);
  const [localSearch, setLocalSearch] = useState(search);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const { environmentStore } = useContext(MobxContext);

  const debouncedOnLocalSearchChange = debounce(setLocalSearch, 200);

  useEffect(() => {
    // If both environment and region are cleared this is a new search
    if (localSearch && !environmentName && !region) {
      setLocalSearch('');
    }
  }, [environmentName, region, localSearch]);

  useEffect(() => {
    setSearchInProgress(false);
    setDeployments(null);
    setPage(0);

    if (environmentStore.environments && environmentName && region) {
      setSearchInProgress(true);

      const cancelTokenSource1 = CancelToken.source();
      const cancelTokenSource2 = CancelToken.source();
      const environment = environmentStore.getEnvironmentByName(environmentName);
      Promise.all([
        windsorApi.getFaroDeployments(search, environment.AccountID, region, true, { cancelToken: cancelTokenSource1.token }),
        windsorApi.getFaroDeployments(search, environment.AccountID, region, false, { cancelToken: cancelTokenSource2.token })
      ]).then(([globalDeployments, localDeployments]) => {
        const allDeployments = [...globalDeployments, ...localDeployments];
        setDeployments(allDeployments.map(item => ({
          name: item.DeploymentName,
          deploymentState: item.State,
          global: item.Global,
          lastModified: moment.utc(item.DeploymentTime).valueOf()
        })));
        setSearchInProgress(false);
      }).catch(error => {
        if (!axios.isCancel(error)) {
          setSearchInProgress(false);
          console.error(`Failed to load deployments:\n${error}`);
        }
      });

      return () => {
        cancelTokenSource1.cancel();
        cancelTokenSource2.cancel();
      };
    }
  }, [environmentStore, environmentStore.environments, environmentName, region, search]);

  const updateQueryParams = (newParams) => {
    let queryString = '';
    Object.keys(newParams).filter(key => newParams[key] !== undefined && newParams[key] !== '').forEach(key => {
      if (queryString.length) {
        queryString += '&';
      }
      queryString += `${key}=${newParams[key]}`;
    });
    if (queryString.length) {
      queryString = `?${queryString}`;
    }
    history.push({ search: queryString })
  };

  const onSearchInputKeyPress = (event) => {
    // Only handle enter key type
    if (event.which === 13) {
      // Clear scheduled execution
      debouncedOnLocalSearchChange.clear();
      setLocalSearch(event.target.value);
      updateQueryParams({ environmentName, region, search: event.target.value });
    }
  }

  const onSearch = (environmentName: string, region: string, search: string) => {
    setLocalSearch(search);
    updateQueryParams({ environmentName, region, search });
  };

  function onPageChange(newPage: number) {
    setPage(newPage);
  }

  const onRegionChange = (event: React.ChangeEvent<{}>, value: any) => {
    if (value) {
      const newRegion = value.code;
      updateQueryParams({ environmentName, region: newRegion, search: environmentName && newRegion ? localSearch : search });
    }
  };

  const onEnvironmentChange = (event: React.ChangeEvent<{}>, value: any) => {
    if (value) {
      const newEnvironment = value.EnvironmentName;
      updateQueryParams({ environmentName: newEnvironment, region, search: newEnvironment && region ? localSearch : search });
    }
  };

  const environment = environmentStore.environments && environmentName ? environmentStore.getEnvironmentByName(environmentName) : null;

  const useLandingPageFormat = !deployments && !searchInProgress;

  return environmentStore.environments ? (
    <div className={classes.root}>
      <div>
        {!useLandingPageFormat && <div>
          <div className={classes.header}>
            <Typography variant="h5" gutterBottom>Deployments</Typography>
          </div>
          <Divider className={classes.divider} light />
        </div>}
        {environmentStore.environments && useLandingPageFormat && !searchInProgress && <FaroDeploymentLandingPageSearch onSearch={onSearch} />}
        {environmentStore.environments && !useLandingPageFormat && <div>
          <div className={classes.toolbar}>
            <div className={classes.toolbarActions}>
              <Autocomplete
                fullWidth
                size="small"
                className={classes.environmentAutocomplete}
                options={environmentStore.environments}
                value={environment}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.Alias}
                onChange={onEnvironmentChange}
                getOptionSelected={(option, value) => value ? option.EnvironmentName === value.EnvironmentName : false}
                renderOption={(option, { selected }) => (
                  <div>
                    <Typography display="block">{option.Alias}</Typography>
                    <Typography variant="caption" display="block">{option.AccountID}</Typography>
                  </div>
                )}
                renderInput={(params) => <TextField
                  {...params}
                  placeholder="Environment"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }} />}
              />
              <Autocomplete
                fullWidth
                size="small"
                className={classes.regionAutocomplete}
                options={AwsRegions || null}
                value={region || null}
                onChange={onRegionChange}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.code}
                getOptionSelected={(option, value) => value ? option.code === value : false}
                renderInput={(params) => <TextField
                  {...params}
                  placeholder="Region"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }} />}
              />
            </div>
            <TextField
              id="search"
              size="small"
              defaultValue={localSearch}
              className={classes.searchTextField}
              fullWidth
              autoComplete="off"
              margin="none"
              variant="outlined"
              placeholder="Search by name (optional)"
              inputProps={{ 'aria-label': 'bare', spellCheck: false }}
              onKeyPress={onSearchInputKeyPress}
              onChange={event => debouncedOnLocalSearchChange(event.target.value)}
            />
          </div>
          {environment && deployments && deployments.length > 0 && <div>
            <Typography color="textSecondary">{deployments.length} deployment{deployments.length !== 1 ? 's' : ''} found</Typography>
            <Paper>
              <FaroDeploymentsTable
                deployments={deployments}
                accountId={environment.AccountID}
                region={region}
                page={page}
                onPageChange={onPageChange}
              />
            </Paper>
          </div>}
          {deployments && search && deployments.length === 0 && <NoResultsText>No deployments matching search</NoResultsText>}
          {deployments && !search && deployments.length === 0 && <NoResultsText>No deployments in the selected environment</NoResultsText>}
          <LoadingIndicator loading={searchInProgress} />
        </div>}
      </div>
    </div>
  ) : <LoadingIndicator loading={true} />;
};

export default withRouter(observer(FaroDeploymentSearchPage));
